import {
  action, autorun, computed, makeObservable, observable, runInAction,
} from 'mobx';
import { Consent, FeatureType } from '@mfe-4cs/types/comsApi';
import { TefComs } from '@mfe-4cs/types/typings';
import { stripHtml } from '@mfe-4cs/util/helpers';
import Service from '@mfe-4cs/util/Service';
import { featuresTypes } from '@mfe-4cs/defs/featureTypes';
import { FeatureValueStore } from './FeatureValueStore';

export class ConsentStore implements Consent {
  changes: number = 0;
  channels?: FeatureValueStore[] = undefined;
  client?: string;
  clientDisplay?: string;
  configId?: string;
  dataTypes?: FeatureValueStore[] = undefined;
  date?: string;
  deprecated?: boolean;
  description?: string;
  granted?: boolean = undefined;
  hintAfter?: string;
  hintBefore?: string;
  id: string;
  name?: string;
  optout?: boolean;
  productTypes?: FeatureValueStore[] = undefined;
  purposeTypes?: FeatureValueStore[] = undefined;
  scope?: string;
  shortScope?: string;
  version?: string;
  modified?: boolean = false;
  modeconstraint?: 'view' | 'edit';

  private $shortInfo?: string;

  private service;

  constructor({ id, shortInfo, ...restData }: Consent) {
    makeObservable(this, {
      changes: observable,
      channels: observable,
      dataTypes: observable,
      granted: observable,
      productTypes: observable,
      purposeTypes: observable,
      modified: observable,
      setModified: action,
      setGranted: action,
      features: computed,
      hasGrantedFeature: computed,
      hasNotGrantedFeature: computed,
      hasAllFeaturesUndefined: computed,
      hasModifiedFeatures: computed,
      shortInfo: computed,
      featureTitlesAsString: computed,
      className: computed,
    });
    this.id = id;
    this.$shortInfo = shortInfo;
    Object.assign(this, restData);
    this.service = new Service();
    featuresTypes.forEach((featureName) => {
      if (restData[featureName]) {
        this[featureName] = restData[featureName]?.map(
          (f) => new FeatureValueStore(f),
        );
      }
    });
    autorun(() => {
      if (this.features.length > 0 && !this.hasAllFeaturesUndefined) {
        this.setModified(this.hasModifiedFeatures || !!this.modified);
        this.setGranted(this.hasGrantedFeature);
      }
    });
  }

  change(value: boolean): void {
    if (value !== this.granted) {
      this.setGrantedWithFeatures(value);
      this.setModified(true);
      this.triggerChange();
    }
  }

  setGrantedWithFeatures(granted: boolean): void {
    this.setGranted(granted);
    this.features.forEach((feature) => {
      feature.setGranted(granted);
    });
  }

  setGranted(granted: boolean): void {
    this.granted = granted;
  }

  setModified(modified: boolean): void {
    this.modified = modified;
  }

  get features(): FeatureValueStore[] {
    // we are assuming that there is always only *one* feature attached
    return this.featureType
      ? (this[this.featureType] as FeatureValueStore[])
      : [];
  }

  get featureType(): FeatureType | undefined {
    // we are assuming that there is always only *one* feature attached
    return featuresTypes.find(
      (featureName: FeatureType) => !!this[featureName],
    );
  }

  get hasGrantedFeature(): boolean {
    return !!this.features.find((f) => f.granted);
  }

  get hasNotGrantedFeature(): boolean {
    return !!this.features.find((f) => !f.granted) && !this.hasAllFeaturesUndefined;
  }

  get hasAllFeaturesUndefined(): boolean {
    return !this.features.find((feature) => feature.isRevokedOrGranted);
  }

  /**
   * Checks if the current consent has been either granted or revoked and
   * verifies that all associated features are also either granted or revoked.
   *
   * @return {boolean} Returns true if the current instance is granted or revoked and all features meet the same condition; otherwise, false.
   */
  get isRevokedOrGranted(): boolean {
    return typeof this.granted !== 'undefined' && this.features.every((f) => f.isRevokedOrGranted);
  }

  get hasModifiedFeatures(): boolean {
    return !!this.features.find((f) => f.modified);
  }

  get className(): string | undefined {
    if (this.modified) {
      return 'edited';
    }
    if (this.deprecated) {
      return 'outdated';
    }
    if (this.granted && this.hasNotGrantedFeature) {
      return 'part_agreed';
    }
    if (this.granted) {
      return 'agreed';
    }
    if (this.granted === undefined) {
      return undefined;
    }
    if (!this.granted) {
      return 'rejected';
    }
    return undefined;
  }

  get shortInfo(): string | undefined {
    const re = /(<span class="coms-features-\w+">)(.*)(<\/span>)/;
    return this.$shortInfo?.replace(re, this.featureTitlesAsString ? `$1${this.featureTitlesAsString}$3` : '$1$3');
  }

  get featureTitlesAsString(): string {
    return this.features
      .filter((f) => f.granted && f.title)
      .map((f) => stripHtml(f.title))
      .join(', ');
  }

  public toJSON(): Consent {
    const ret: Consent = {
      ...this,
    };

    featuresTypes.forEach((featureType) => {
      if (this[featureType]) {
        ret[featureType] = this[featureType]?.map((feature) => feature.toJSON());
      }
    });

    return ret;
  }

  public toSaveConsent(): TefComs.ConsentStatus {
    const {
      id, granted = false, featureType, features,
    } = this;
    return {
      type: 'consent',
      id,
      granted,
      ...(featureType
        ? { [featureType]: features.map((feature) => feature.toSaveFeatureValue()) }
        : {}
      ),
    };
  }

  public toShortConsent(): TefComs.ShortConsent {
    const {
      id,
      granted = false,
      featureType,
      features,
      shortInfo = '',
      version = '',
    } = this;
    return {
      id,
      granted,
      ...(featureType
        ? { [featureType]: features.map((feature) => feature.toShortFeatureValue()) }
        : {}
      ),
      shortInfo,
      version,
    };
  }

  public triggerChange(): void {
    runInAction(() => {
      this.changes += 1;
    });
    this.service.fireEvent<TefComs.EventDetails>({
      name: 'consents/CHANGE',
      level: 'info',
      message: 'user changed a consent',
    });
  }
}
